import { useEffect, useMemo, useRef, useState } from "react";
import type { ProjectManifest, ProjectResp } from "@pix/shared";
import { ScriptEditor } from "../components/ScriptEditor";
import { FileTree } from "../components/FileTree";
import { TestPlay, type TestPlayHandle } from "../components/TestPlay";
import { Toolbar, type PreviewPosition } from "../components/Toolbar";
import { AssetPanel } from "../components/AssetPanel";
import { SceneGrid, type SceneGridHandle } from "../components/SceneGrid";
import { SampleView } from "../components/SampleView";
import { Inspector } from "../components/Inspector";
import { ManifestForm } from "../components/ManifestForm";
import { SceneFlow } from "../components/SceneFlow";
import { PublishModal } from "../components/PublishModal";
import { FindReplaceModal } from "../components/FindReplaceModal";
import { ShortcutsModal } from "../components/ShortcutsModal";
import { LintPanel } from "../components/LintPanel";
import { lintProject } from "../grid/lint";
import { SnippetsPanel } from "../components/SnippetsPanel";
import { OutlinePanel } from "../components/OutlinePanel";
import type { SceneRow } from "../grid/gridTypes";
import { useAuth } from "../auth/AuthContext";
import { api } from "../api/client";
import { buildDataUrlDict } from "../storage/assetStore";
import { useFileEditHistory } from "../hooks/useEditHistory";

const ENGINE_URL =
  (import.meta.env.VITE_ENGINE_URL as string | undefined) ?? "https://play.pixadv.net";
const MANIFEST_KEY = "__manifest__";
const LOG_LIMIT = 60;

type LeftTab = "files" | "assets" | "lint" | "snips" | "outline";
type CenterTab = "grid" | "script" | "flow";
type ManifestTab = "form" | "json";

interface Props {
  project: ProjectResp;
  onLeave: () => void;
}

export function StudioView({ project, onLeave }: Props) {
  const { user, logout } = useAuth();
  const [manifest, setManifest] = useState<ProjectManifest>(project.manifest);
  const [manifestText, setManifestText] = useState<string>(() =>
    JSON.stringify(project.manifest, null, 2),
  );
  const [scripts, setScripts] = useState<Record<string, string>>(project.scripts);
  const [activeFile, setActiveFile] = useState<string>(
    Object.keys(project.scripts).sort()[0] ?? MANIFEST_KEY,
  );
  const [leftTab, setLeftTab] = useState<LeftTab>("files");
  const [centerTab, setCenterTab] = useState<CenterTab>("grid");
  const [manifestTab, setManifestTab] = useState<ManifestTab>("form");
  const [previewPosition, setPreviewPosition] = useState<PreviewPosition>(() => {
    try {
      const v = localStorage.getItem("pix-studio:previewPosition");
      if (v === "right" || v === "top" || v === "hidden") return v;
    } catch {
      /* ignore */
    }
    return "right";
  });
  const changePreviewPosition = (pos: PreviewPosition) => {
    setPreviewPosition(pos);
    try {
      localStorage.setItem("pix-studio:previewPosition", pos);
    } catch {
      /* ignore */
    }
  };
  const [logLines, setLogLines] = useState<string[]>([]);
  const [isDirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedRow, setSelectedRow] = useState<SceneRow | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [findOpen, setFindOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  /** LINT / OUTLINE クリックで「ファイル切替後に該当行へジャンプ」を指示する */
  const [pendingJumpLine, setPendingJumpLine] = useState<number | null>(null);
  const testPlayRef = useRef<TestPlayHandle>(null);
  const gridRef = useRef<SceneGridHandle>(null);

  const getFileValue = (file: string): string => {
    if (file === MANIFEST_KEY) return manifestText;
    return scripts[file] ?? "";
  };
  const setFileValue = (file: string, value: string) => {
    if (file === MANIFEST_KEY) {
      setManifestText(value);
      try {
        setManifest(JSON.parse(value) as ProjectManifest);
      } catch {
        /* JSON 不正中は manifest を更新しない */
      }
    } else {
      setScripts((prev) => ({ ...prev, [file]: value }));
    }
    setDirty(true);
  };
  const history = useFileEditHistory({
    activeFile,
    getValue: getFileValue,
    setValue: setFileValue,
  });

  const projectName = useMemo(() => project.name, [project]);
  const projectId = project.id;

  const appendLog = (line: string) => {
    setLogLines((prev) => {
      const next = [...prev, line];
      if (next.length > LOG_LIMIT) next.shift();
      return next;
    });
  };

  const onSelectFile = (name: string) => {
    setActiveFile(name);
    setLeftTab("files");
    setSelectedRow(null);
  };

  const onCreateFile = (name: string) => {
    const initial = "## start\n\n> 新規シーン\n";
    setScripts((prev) => ({ ...prev, [name]: initial }));
    // manifest.scenes に追加
    setManifest((m) => {
      const scenes = Array.isArray(m.scenes) ? m.scenes : [];
      if (scenes.includes(name)) return m;
      const next = { ...m, scenes: [...scenes, name] };
      setManifestText(JSON.stringify(next, null, 2));
      return next;
    });
    setDirty(true);
    setActiveFile(name);
    setSelectedRow(null);
    appendLog(`[studio] 新規ファイル: ${name}`);
  };

  const onRenameFile = (oldName: string, newName: string) => {
    setScripts((prev) => {
      const next: Record<string, string> = {};
      for (const [k, v] of Object.entries(prev)) {
        next[k === oldName ? newName : k] = v;
      }
      return next;
    });
    setManifest((m) => {
      const next: ProjectManifest = {
        ...m,
        scenes: (m.scenes ?? []).map((s) => (s === oldName ? newName : s)),
        entry: m.entry === oldName ? newName : m.entry,
      };
      setManifestText(JSON.stringify(next, null, 2));
      return next;
    });
    setDirty(true);
    if (activeFile === oldName) setActiveFile(newName);
    appendLog(`[studio] リネーム: ${oldName} → ${newName}`);
  };

  const onDeleteFile = (name: string) => {
    setScripts((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
    setManifest((m) => {
      const scenes = (m.scenes ?? []).filter((s) => s !== name);
      const next: ProjectManifest = {
        ...m,
        scenes,
        entry: m.entry === name ? scenes[0] ?? m.entry : m.entry,
      };
      setManifestText(JSON.stringify(next, null, 2));
      return next;
    });
    setDirty(true);
    if (activeFile === name) {
      const remain = Object.keys(scripts).filter((f) => f !== name);
      setActiveFile(remain[0] ?? MANIFEST_KEY);
    }
    appendLog(`[studio] 削除: ${name}`);
  };

  const onEditScript = (next: string) => {
    if (activeFile === MANIFEST_KEY) {
      setManifestText(next);
      setDirty(true);
      try {
        const parsed = JSON.parse(next) as ProjectManifest;
        setManifest(parsed);
      } catch {
        /* シンタックスエラー中はスルー */
      }
    } else {
      setScripts((prev) => ({ ...prev, [activeFile]: next }));
      setDirty(true);
    }
  };

  const onSave = async () => {
    setSaving(true);
    try {
      let m = manifest;
      try {
        m = JSON.parse(manifestText) as ProjectManifest;
        setManifest(m);
      } catch (e) {
        appendLog(`[studio error] manifest JSON が不正: ${(e as Error).message}`);
        return;
      }
      await api.updateProject(projectId, { manifest: m, scripts });
      setDirty(false);
      appendLog("[studio] クラウドに保存しました");
    } catch (e) {
      appendLog(`[studio error] 保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  const onRun = async () => {
    let m = manifest;
    try {
      m = JSON.parse(manifestText) as ProjectManifest;
    } catch (e) {
      appendLog(`[studio error] manifest が不正: ${(e as Error).message}`);
      return;
    }
    const assets = await buildDataUrlDict(projectId);
    const count = Object.keys(assets).length;
    if (count > 0) appendLog(`[studio] アセット ${count} 件を同梱して送信`);
    testPlayRef.current?.loadAndStart(m, scripts, assets);
    appendLog("[studio] エンジンに送信、実行開始");
  };

  // グローバルホットキー
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const cmd = e.ctrlKey || e.metaKey;
      if (cmd && e.shiftKey && (e.key === "F" || e.key === "f")) {
        e.preventDefault();
        setFindOpen(true);
      } else if (cmd && e.key === "s") {
        e.preventDefault();
        void onSave();
      } else if (e.key === "F5") {
        e.preventDefault();
        void onRun();
      } else if (e.key === "F1" || (e.key === "?" && !cmd)) {
        // ? は通常 Shift+/ なので INPUT/TEXTAREA 内では拾わない
        const tag = (e.target as HTMLElement | null)?.tagName ?? "";
        if (tag !== "INPUT" && tag !== "TEXTAREA") {
          e.preventDefault();
          setShortcutsOpen(true);
        }
      } else if (e.key === "F12") {
        // 選択行から @jump / @call / 選択肢の遷移先へ移動
        const row = selectedRow;
        if (!row) return;
        const target = findJumpTarget(row, activeFile);
        if (target) {
          e.preventDefault();
          if (target.file !== activeFile) setActiveFile(target.file);
          // 対象ファイルの該当ラベル行を探す
          const src = target.file === activeFile ? (scripts[target.file] ?? "") : (scripts[target.file] ?? "");
          const line = findLabelLine(src, target.label);
          if (line) setPendingJumpLine(line);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifestText, scripts, manifest]);

  // オートセーブ: dirty 状態 + 2 秒の静止 で保存
  const autoSaveTimer = useRef<number | null>(null);
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  useEffect(() => {
    if (!isDirty || saving) return;
    if (autoSaveTimer.current !== null) {
      window.clearTimeout(autoSaveTimer.current);
    }
    autoSaveTimer.current = window.setTimeout(() => {
      void onSaveRef.current();
    }, 2000);
    return () => {
      if (autoSaveTimer.current !== null) {
        window.clearTimeout(autoSaveTimer.current);
      }
    };
  }, [isDirty, saving, manifestText, scripts]);

  // 起動時に自動 run
  const [didAutoRun, setDidAutoRun] = useState(false);
  useEffect(() => {
    if (!didAutoRun) {
      const t = window.setTimeout(() => {
        void onRun();
        setDidAutoRun(true);
      }, 600);
      return () => window.clearTimeout(t);
    }
    return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [didAutoRun]);

  const currentValue =
    activeFile === MANIFEST_KEY ? manifestText : scripts[activeFile] ?? "";

  const filesList = Object.keys(scripts);

  // LINT バッジ: error 件数のみ(警告は数字を出さない)
  const lintBadge = useMemo(() => {
    const r = lintProject(scripts, manifest);
    const errors = r.issues.filter((i) => i.severity === "error").length;
    return errors > 0 ? errors : 0;
  }, [scripts, manifest]);

  /** スニペット挿入:
   *   - グリッド表示中で行が選択されていれば、その上に挿入
   *   - それ以外は現在ファイル末尾に追記 */
  const insertSnippet = (text: string) => {
    if (activeFile === MANIFEST_KEY) return; // manifest には挿入不可
    history.push(activeFile, scripts[activeFile] ?? "");
    const src = scripts[activeFile] ?? "";
    let next: string;
    if (centerTab === "grid" && selectedRow) {
      // selectedRow.sourceLine の直前に空行込みで挿入
      const lines = src.split(/\r?\n/);
      const idx = Math.max(0, selectedRow.sourceLine - 1);
      const insertion = text.split(/\r?\n/);
      lines.splice(idx, 0, ...insertion, "");
      next = lines.join("\n");
    } else {
      const sep = src.endsWith("\n") ? "" : "\n";
      next = src + sep + text + "\n";
    }
    setScripts((prev) => ({ ...prev, [activeFile]: next }));
    setDirty(true);
    appendLog(`[studio] スニペット挿入: ${text.split("\n")[0]?.slice(0, 30) ?? ""}`);
  };

  return (
    <div className="studio-app">
      <Toolbar
        title={projectName}
        isDirty={isDirty}
        saving={saving}
        userName={user?.displayName ?? ""}
        previewPosition={previewPosition}
        canUndo={history.canUndo()}
        canRedo={history.canRedo()}
        onSave={() => void onSave()}
        onRun={() => void onRun()}
        onLeave={onLeave}
        onLogout={logout}
        onChangePreviewPosition={changePreviewPosition}
        onUndo={history.undo}
        onRedo={history.redo}
        onPublish={() => setPublishOpen(true)}
      />
      {publishOpen && (
        <PublishModal
          project={project}
          manifest={manifest}
          onClose={() => setPublishOpen(false)}
          onLog={appendLog}
        />
      )}
      {shortcutsOpen && <ShortcutsModal onClose={() => setShortcutsOpen(false)} />}
      {findOpen && (
        <FindReplaceModal
          files={scripts}
          activeFile={activeFile}
          onClose={() => setFindOpen(false)}
          onReplace={(file, src) => {
            history.push(file, scripts[file] ?? "");
            setScripts((prev) => ({ ...prev, [file]: src }));
            setDirty(true);
          }}
          onSelectFile={(file, line) => {
            setActiveFile(file);
            setFindOpen(false);
            // line ジャンプは後続対応(まずファイル切替まで)
            void line;
          }}
        />
      )}
      {previewPosition === "top" && (
        <div className="studio-top-preview">
          <TestPlay ref={testPlayRef} engineUrl={ENGINE_URL} onLog={appendLog} />
          <div className="log log-top">
            {logLines.length === 0 ? (
              <div className="log-empty">(no events yet)</div>
            ) : (
              logLines.map((l, i) => (
                <div key={i} className="log-line">
                  {l}
                </div>
              ))
            )}
          </div>
        </div>
      )}
      <div className={"studio-main" + (previewPosition !== "right" ? " no-preview" : "")}>
        <aside className="pane pane-left">
          <div className="left-tabs">
            <button
              className={"left-tab" + (leftTab === "files" ? " active" : "")}
              onClick={() => setLeftTab("files")}
            >
              FILES
            </button>
            <button
              className={"left-tab" + (leftTab === "assets" ? " active" : "")}
              onClick={() => setLeftTab("assets")}
            >
              ASSETS
            </button>
            <button
              className={"left-tab" + (leftTab === "lint" ? " active" : "")}
              onClick={() => setLeftTab("lint")}
              title="検証"
            >
              LINT{lintBadge && <span className="left-tab-badge">{lintBadge}</span>}
            </button>
            <button
              className={"left-tab" + (leftTab === "snips" ? " active" : "")}
              onClick={() => setLeftTab("snips")}
              title="スニペット"
            >
              SNIPS
            </button>
            <button
              className={"left-tab" + (leftTab === "outline" ? " active" : "")}
              onClick={() => setLeftTab("outline")}
              title="アウトライン (ラベル / マクロ)"
            >
              ⌥
            </button>
          </div>
          <div className="pane-body">
            {leftTab === "files" ? (
              <FileTree
                files={filesList}
                activeFile={activeFile}
                onSelect={onSelectFile}
                onCreate={onCreateFile}
                onRename={onRenameFile}
                onDelete={onDeleteFile}
              />
            ) : leftTab === "assets" ? (
              <AssetPanel projectId={projectId} />
            ) : leftTab === "lint" ? (
              <LintPanel
                scripts={scripts}
                manifest={manifest}
                onJump={(file, line) => {
                  setActiveFile(file);
                  setPendingJumpLine(line);
                }}
              />
            ) : leftTab === "snips" ? (
              <SnippetsPanel manifest={manifest} onInsert={insertSnippet} />
            ) : (
              <OutlinePanel
                scripts={scripts}
                activeFile={activeFile}
                onJump={(file, line) => {
                  setActiveFile(file);
                  setPendingJumpLine(line);
                }}
              />
            )}
          </div>
        </aside>
        <section className="pane pane-center">
          <div className="pane-header pane-header-with-tabs">
            <span className="pane-filename">
              {activeFile === MANIFEST_KEY ? "project.pix.json" : activeFile}
            </span>
            {activeFile === MANIFEST_KEY ? (
              <div className="center-tabs">
                <button
                  className={"center-tab" + (manifestTab === "form" ? " active" : "")}
                  onClick={() => setManifestTab("form")}
                >
                  FORM
                </button>
                <button
                  className={"center-tab" + (manifestTab === "json" ? " active" : "")}
                  onClick={() => setManifestTab("json")}
                >
                  JSON
                </button>
              </div>
            ) : (
              <div className="center-tabs">
                <button
                  className={"center-tab" + (centerTab === "grid" ? " active" : "")}
                  onClick={() => setCenterTab("grid")}
                >
                  SCENE
                </button>
                <button
                  className={"center-tab" + (centerTab === "script" ? " active" : "")}
                  onClick={() => setCenterTab("script")}
                >
                  SCRIPT
                </button>
                <button
                  className={"center-tab" + (centerTab === "flow" ? " active" : "")}
                  onClick={() => setCenterTab("flow")}
                >
                  FLOW
                </button>
              </div>
            )}
          </div>
          <div className="pane-body">
            {activeFile === MANIFEST_KEY ? (
              manifestTab === "form" ? (
                <div className="manifest-form-wrap">
                  <ManifestForm
                    manifest={manifest}
                    projectId={project.id}
                    scriptFiles={Object.keys(scripts)}
                    onChange={(next) => {
                      history.push(MANIFEST_KEY, manifestText);
                      setManifest(next);
                      setManifestText(JSON.stringify(next, null, 2));
                      setDirty(true);
                    }}
                  />
                </div>
              ) : (
                <ScriptEditor
                  value={currentValue}
                  onChange={onEditScript}
                  filename={activeFile}
                  scrollToLine={pendingJumpLine}
                  onScrolled={() => setPendingJumpLine(null)}
                  manifest={manifest}
                />
              )
            ) : centerTab === "script" ? (
              <ScriptEditor
                value={currentValue}
                onChange={onEditScript}
                filename={activeFile}
                scrollToLine={pendingJumpLine}
                onScrolled={() => setPendingJumpLine(null)}
                manifest={manifest}
              />
            ) : centerTab === "flow" ? (
              <SceneFlow
                scripts={scripts}
                activeFile={activeFile}
                onSelect={(file) => {
                  if (file !== activeFile && scripts[file] !== undefined) {
                    setActiveFile(file);
                    setSelectedRow(null);
                  }
                  setCenterTab("grid");
                }}
              />
            ) : (
              <SceneGrid
                ref={gridRef}
                source={currentValue}
                filename={activeFile}
                manifest={manifest}
                projectId={projectId}
                engineUrl={ENGINE_URL}
                activeRowId={selectedRow?.id}
                onRowSelect={setSelectedRow}
                onSourceChange={(newSource) => {
                  history.push(activeFile, scripts[activeFile] ?? "");
                  setScripts((prev) => ({ ...prev, [activeFile]: newSource }));
                  setDirty(true);
                }}
                onManifestChange={(next) => {
                  history.push(MANIFEST_KEY, manifestText);
                  setManifest(next);
                  setManifestText(JSON.stringify(next, null, 2));
                  setDirty(true);
                }}
                scrollToLine={pendingJumpLine}
                onScrolled={() => setPendingJumpLine(null)}
              />
            )}
          </div>
        </section>
        {previewPosition === "right" && (
          <section className="pane pane-right">
            <div className="pane-section pane-section-sample">
              <div className="pane-section-header">SNAPSHOT</div>
              <SampleView row={selectedRow} manifest={manifest} projectId={projectId} />
            </div>
            <div className="pane-section pane-section-inspector">
              <div className="pane-section-header">INSPECTOR</div>
              <Inspector
                row={selectedRow}
                manifest={manifest}
                onEditMessage={(r) => gridRef.current?.editMessage(r)}
                onEditChoices={(r) => gridRef.current?.editChoices(r)}
                onEditBgm={(r) => gridRef.current?.editBgm(r)}
                onEditBg={(r) => gridRef.current?.editBg(r)}
                onEditSe={(r) => gridRef.current?.editSe(r)}
                onEditChara={(r, p) => gridRef.current?.editChara(r, p)}
                onEditJump={(r) => gridRef.current?.editJump(r)}
                onEditLabel={(r) => gridRef.current?.editLabel(r)}
              />
            </div>
            <TestPlay ref={testPlayRef} engineUrl={ENGINE_URL} onLog={appendLog} />
            <div className="log">
              {logLines.length === 0 ? (
                <div className="log-empty">(no events yet)</div>
              ) : (
                logLines.map((l, i) => (
                  <div key={i} className="log-line">
                    {l}
                  </div>
                ))
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

/** 選択行 (SceneRow) のジャンプ先 (target file, label) を取り出す */
function findJumpTarget(
  row: SceneRow,
  currentFile: string,
): { file: string; label: string } | null {
  for (const m of row.markers) {
    if (m.kind === "jump" || m.kind === "call") {
      const raw = (m as { target: string }).target;
      if (!raw || raw === "?") return null;
      if (raw.startsWith("#")) return { file: currentFile, label: raw.slice(1) };
      const i = raw.indexOf("#");
      if (i > 0) return { file: raw.slice(0, i), label: raw.slice(i + 1) };
      return { file: currentFile, label: raw };
    }
  }
  if (row.choices && row.choices.length > 0) {
    const c = row.choices[0]!;
    const t = c.target;
    if (t.startsWith("#")) return { file: currentFile, label: t.slice(1) };
    const i = t.indexOf("#");
    if (i > 0) return { file: t.slice(0, i), label: t.slice(i + 1) };
    return { file: currentFile, label: t };
  }
  return null;
}

/** ファイル内で ## label_name の行番号を探す */
function findLabelLine(source: string, label: string): number | null {
  const lines = source.split(/\r?\n/);
  const target = `## ${label}`;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i]!.trim() === target) return i + 1;
  }
  return null;
}
